import type { Photo } from '../types';

type UrlMap = Record<string, string>;

const FILENAME_RE = /^(.+)-(\d+)\.(jpg|webp)$/;

function captionFromBasename(basename: string): string {
  return basename
    .replace(/^\d+-/, '')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Pairs the JPEG + WebP URL maps emitted by `import.meta.glob` into a sorted
 * Photo[]. Each photo basename is expected to share the form `NN-slug-WIDTH.{jpg,webp}`,
 * matching what scripts/generate-photos.mjs writes.
 */
export function buildPhotos(jpgs: UrlMap, webps: UrlMap): Photo[] {
  type Variant = { width: number; jpg?: string; webp?: string };
  const groups = new Map<string, Map<number, Variant>>();

  const ingest = (urls: UrlMap, kind: 'jpg' | 'webp') => {
    for (const [pathKey, url] of Object.entries(urls)) {
      const filename = pathKey.split('/').pop();
      if (!filename) continue;
      const match = FILENAME_RE.exec(filename);
      if (!match) continue;
      const basename = match[1];
      const widthStr = match[2];
      const ext = match[3];
      if (!basename || !widthStr || ext !== kind) continue;
      const width = Number(widthStr);
      const variants = groups.get(basename) ?? new Map<number, Variant>();
      const v = variants.get(width) ?? { width };
      v[kind] = url;
      variants.set(width, v);
      groups.set(basename, variants);
    }
  };

  ingest(jpgs, 'jpg');
  ingest(webps, 'webp');

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([basename, variants]) => {
      const sizes = [...variants.values()].sort((a, b) => a.width - b.width);
      const jpgSrcSet = sizes
        .filter((s) => s.jpg)
        .map((s) => `${s.jpg} ${s.width}w`)
        .join(', ');
      const webpSrcSet = sizes
        .filter((s) => s.webp)
        .map((s) => `${s.webp} ${s.width}w`)
        .join(', ');
      const fallback = sizes[sizes.length - 1]?.jpg ?? sizes[0]?.jpg ?? '';
      return {
        caption: captionFromBasename(basename),
        jpgSrcSet,
        webpSrcSet,
        fallback,
        maxWidth: sizes[sizes.length - 1]?.width ?? 0,
      };
    });
}
